import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton, Alert, Box, Button, CircularProgress,
  Typography, Stack, Link,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WifiTetheringRoundedIcon from '@mui/icons-material/WifiTetheringRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import api from '../services/api';
import MarketplaceIcon from './common/MarketplaceIcon';
import { marketplaceMeta, tokens } from '../theme';

const emptyForm = (marketplace) => ({ name: '', marketplace, api_token: '', client_id: '', campaign_id: null, business_id: null });

const HOW_TO = {
  wildberries: {
    href: 'https://seller.wildberries.ru',
    site: 'личный кабинет продавца',
    steps: [
      'Откройте «Настройки» → «Доступ к API»',
      'Создайте персональный токен («Для интеграции вручную») с доступом «Только чтение» и отметьте все категории — минимум «Статистика», «Аналитика», «Финансы», «Контент», «Продвижение» и «Поставки». Базовый и тестовый токены не подходят',
      'Скопируйте токен и вставьте его ниже',
    ],
  },
  ozon: {
    href: 'https://seller.ozon.ru',
    site: 'личный кабинет продавца Ozon',
    steps: ['Откройте «Настройки» → «Seller API»', 'Создайте ключ — Client-Id и Api-Key появятся в списке ключей', 'Скопируйте оба значения ниже'],
  },
  yandex_market: {
    href: 'https://partner.market.yandex.ru',
    site: 'личный кабинет Яндекс Маркета',
    steps: ['Откройте раздел интеграций и создайте API-ключ', 'Вставьте ключ ниже и нажмите «Проверить»', 'Магазин определится автоматически'],
  },
};

// Add or edit one marketplace account. `account` set = edit mode (name and, optionally, a new key).
// onClose(null) = cancelled, onClose({ created }) / onClose({ updated: true }) = saved.
const ConnectMarketplaceDialog = ({ open, marketplace, account, onClose }) => {
  const editMode = Boolean(account);
  const mp = account?.marketplace || marketplace || 'wildberries';
  const meta = marketplaceMeta(mp);
  const [form, setForm] = useState(emptyForm(mp));
  const [error, setError] = useState('');
  const [taken, setTaken] = useState(false); // the cabinet belongs to another MARQIT account
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [campaigns, setCampaigns] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({ ...emptyForm(mp), name: account?.name || '' });
    setError('');
    setTestResult(null);
    setCampaigns(null);
    setTesting(false);
  }, [open, mp, account]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const credentials = () => {
    const payload = { api_token: form.api_token.trim() };
    if (mp === 'ozon') payload.client_id = form.client_id.trim();
    if (mp === 'yandex_market' && form.campaign_id && form.business_id) {
      payload.campaign_id = form.campaign_id;
      payload.business_id = form.business_id;
    }
    return payload;
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = { marketplace: mp, api_token: form.api_token.trim() };
      if (mp === 'ozon') payload.client_id = form.client_id.trim();
      const { data } = await api.post('/marketplace/accounts/test-connection', payload);
      const list = data?.campaigns || [];
      if (mp === 'yandex_market' && list.length > 1) {
        setCampaigns(list);
        setTestResult({ ok: true, text: `Найдено магазинов: ${list.length}. Выберите нужный.` });
      } else if (mp === 'yandex_market' && list.length === 1) {
        setCampaigns(null);
        set({ campaign_id: list[0].campaign_id, business_id: list[0].business_id });
        setTestResult({ ok: true, text: 'Доступ есть. Магазин определён автоматически.' });
      } else {
        setCampaigns(null);
        setTestResult({ ok: true, text: 'Доступ есть.' });
      }
    } catch (e) {
      setCampaigns(null);
      setTestResult({ ok: false, text: e.response?.data?.error || 'Не удалось проверить доступ' });
    } finally {
      setTesting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim()) return setError('Укажите название кабинета');
    if (!editMode && !form.api_token.trim()) return setError('Вставьте API-ключ');
    if (!editMode && mp === 'ozon' && !form.client_id.trim()) return setError('Укажите Client-Id');
    setSubmitting(true);
    setError('');
    setTaken(false);
    try {
      if (editMode) {
        const body = { name: form.name.trim() };
        if (form.api_token.trim()) Object.assign(body, credentials());
        else if (mp === 'ozon' && form.client_id.trim()) body.client_id = form.client_id.trim();
        await api.put(`/marketplace/accounts/${account.id}`, body);
        onClose({ updated: true });
      } else {
        const { data } = await api.post('/marketplace/accounts', { name: form.name.trim(), marketplace: mp, ...credentials() });
        onClose({ created: data });
      }
    } catch (err) {
      if (err.response?.status === 422 && err.response?.data?.error_code === 'campaign_selection_required') {
        setError('У этого ключа несколько магазинов — выберите нужный ниже.');
        await test();
      } else {
        setTaken(err.response?.data?.error_code === 'account_taken');
        setError(err.response?.data?.error || 'Не удалось сохранить подключение');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const how = HOW_TO[mp];
  return (
    <Dialog open={open} onClose={() => !submitting && onClose(null)} maxWidth="sm" fullWidth
      PaperProps={{ component: 'form', onSubmit: submit, noValidate: true }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 7 }}>
        <MarketplaceIcon marketplace={mp} size={40} />
        <Box component="span" sx={{ display: 'block' }}>
          <Box component="span" sx={{ display: 'block', fontWeight: 700, fontSize: '1.15rem', lineHeight: 1.3 }}>
            {editMode ? `Кабинет ${meta.label}` : `Подключить ${meta.label}`}
          </Box>
          <Box component="span" sx={{ display: 'block', fontWeight: 400, fontSize: '0.875rem', color: tokens.inkSoft }}>
            {editMode ? 'Измените название или замените API-ключ' : 'Данные загрузятся автоматически после подключения'}
          </Box>
        </Box>
        <IconButton onClick={() => onClose(null)} aria-label="Закрыть" sx={{ position: 'absolute', right: 16, top: 16 }} disabled={submitting}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        {!editMode && how && (
          <Box sx={{ bgcolor: tokens.primaryTint, border: `1px solid ${tokens.border}`, borderRadius: '12px', p: 2, mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Где взять ключ: <Link href={how.href} target="_blank" rel="noopener noreferrer">{how.site}</Link>
            </Typography>
            <Stack spacing={0.75}>
              {how.steps.map((s, i) => (
                <Stack key={s} direction="row" spacing={1.25} alignItems="flex-start">
                  <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: tokens.primary, color: '#fff', fontSize: '0.75rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: '1px' }}>
                    {i + 1}
                  </Box>
                  <Typography variant="body2">{s}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        <Stack spacing={2}>
          <TextField fullWidth autoFocus label="Название кабинета" value={form.name} onChange={(e) => set({ name: e.target.value })}
            placeholder="Например: Основной магазин" helperText="Видно только вам — чтобы различать кабинеты" required />
          {mp === 'ozon' && (
            <TextField fullWidth label="Client-Id" value={form.client_id} onChange={(e) => set({ client_id: e.target.value })}
              required={!editMode} placeholder={editMode ? 'Оставьте пустым, чтобы не менять' : ''} />
          )}
          <TextField fullWidth type="password" autoComplete="off" label={mp === 'wildberries' ? 'API-токен' : 'Api-Key'} value={form.api_token}
            onChange={(e) => set({ api_token: e.target.value })} required={!editMode}
            placeholder={editMode ? 'Оставьте пустым, чтобы не менять' : ''}
            helperText={editMode ? 'Оставьте пустым, если ключ не меняется' : ' '} />
        </Stack>

        {mp === 'yandex_market' && (
          <Box sx={{ mt: 1 }}>
            <Button variant="outlined" size="small" onClick={test} disabled={testing || !form.api_token.trim()}
              startIcon={testing ? <CircularProgress size={16} /> : <WifiTetheringRoundedIcon />}>
              Проверить
            </Button>
            {campaigns?.length > 0 && (
              <TextField select fullWidth label="Магазин" value="" sx={{ mt: 2 }}
                onChange={(e) => {
                  const c = campaigns.find((x) => `${x.campaign_id}` === e.target.value);
                  if (c) {
                    set({ campaign_id: c.campaign_id, business_id: c.business_id });
                    setCampaigns(null);
                    setTestResult({ ok: true, text: 'Магазин выбран.' });
                  }
                }}>
                {campaigns.map((c) => (
                  <MenuItem key={c.campaign_id} value={`${c.campaign_id}`}>{c.domain || `Кампания ${c.campaign_id}`} (бизнес {c.business_id})</MenuItem>
                ))}
              </TextField>
            )}
          </Box>
        )}
        {testResult && <Alert severity={testResult.ok ? 'success' : 'error'} sx={{ mt: 2 }}>{testResult.text}</Alert>}
        {!editMode && mp !== 'wildberries' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Подключение и проверка доступа работают уже сейчас. Загрузка данных {meta.label} появится в ближайшем обновлении — кабинет подтянется автоматически.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}
            action={taken ? (
              <Button color="inherit" size="small" component="a" href="/forgot-password" target="_blank" rel="noopener">
                Восстановить доступ
              </Button>
            ) : undefined}>
            {error}
          </Alert>
        )}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2, color: tokens.inkSoft }}>
          <LockOutlinedIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption">Ключ хранится на сервере в зашифрованном виде и больше не показывается.</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(null)} disabled={submitting}>Отмена</Button>
        <Button type="submit" variant="contained" disabled={submitting} startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
          {editMode ? 'Сохранить' : 'Подключить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectMarketplaceDialog;
